import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/client';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import ConversationPanel from './ConversationPanel/ConversationPanel';
import ChatPanel from './ChatPanel/ChatPanel';
import { isSameUser } from './shared/identity';

const GET_CONVERSATIONS = gql`
  query GetPersonalConversations {
    getPersonalConversations {
      id
      participant1 {
        id
        name
        allowed_name
        user_name
        fb_user_name
        profile_picture
        is_student
        student_avatar_url
        advisor_firm_name
      }
      participant2 {
        id
        name
        allowed_name
        user_name
        fb_user_name
        profile_picture
        is_student
        student_avatar_url
        advisor_firm_name
      }
      conversationType
      blockedByParticipant1
      blockedByParticipant2
      archivedByParticipant1
      archivedByParticipant2
      partialSaleListingUser {
        id
        partial_sale_listing {
          id
          segment_details
          asking_price
          number_of_clients
          assets_under_management
          sale_status
        }
        buyer {
          id
          name
          allowed_name
          user_name
          fb_user_name
          profile_picture
        }
        seller {
          id
          name
          allowed_name
          user_name
          fb_user_name
          profile_picture
        }
      }
      lastMessages(limit: 1) {
        id
        body
        createdAt
        read
        messageType
        # App-authored notes (e.g. the Partial Sale Meeting Room invitation) have
        # no sender; ConversationItem previews them without an "Unknown:" prefix.
        system_authored
        sender {
          id
          name
          allowed_name
          user_name
          fb_user_name
        }
      }
    }
  }
`;

const MARK_MESSAGES_AS_READ = gql`
  mutation MarkMessagesAsRead($conversationId: ID!) {
    markMessagesAsRead(conversationId: $conversationId)
  }
`;

/**
 * MessagingHub - Modern messaging interface with succession theming
 * 
 * This component provides a themed messaging container with proper succession styling
 * @param {Object} props
 * @param {Object} props.currentUser - Current user object
 * @param {string} props.theme - Theme for the messaging interface (light/dark)
 * @param {string} props.initialConversationId - Optional conversation ID to auto-select
 * @param {string} props.initialHiringPostId - Optional hiring post ID to create/find conversation for
 */
const MessagingHub = ({ currentUser, theme = 'light', initialConversationId = null, initialHiringPostId = null }) => {
  const intl = useIntl();
  console.log('[MessagingHub] Props received:');
  console.log('  currentUser:', currentUser);
  console.log('  currentUser.id:', currentUser?.id);
  console.log('  initialConversationId:', initialConversationId);
  console.log('  theme:', theme);
  
  const { theme: successionTheme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [selectedConversationId, setSelectedConversationId] = useState(initialConversationId);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [readConversationIds, setReadConversationIds] = useState(new Set());
  const [lastGoodData, setLastGoodData] = useState(null);

  // GraphQL hooks
  const { loading, error, data, refetch, stopPolling } = useQuery(GET_CONVERSATIONS, {
    pollInterval: 300000,
    errorPolicy: 'all',
    fetchPolicy: 'network-only', // Always fetch fresh data to ensure read status is up-to-date
    nextFetchPolicy: 'cache-first' // After initial fetch, use cache for subsequent renders
  });

  // Preserve last successful data so the UI stays intact on errors
  useEffect(() => {
    if (data?.getPersonalConversations) {
      setLastGoodData(data);
    }
  }, [data]);

  // Stop polling on any error — polling a broken request just generates noise
  useEffect(() => {
    if (error) {
      stopPolling();
    }
  }, [error]);

  // Use last good data if current data is wiped by an error
  const effectiveData = data?.getPersonalConversations ? data : lastGoodData;

  const [markMessagesAsRead] = useMutation(MARK_MESSAGES_AS_READ, {
    // Refetch conversations after marking as read to update cache
    refetchQueries: ['GetPersonalConversations'],
    awaitRefetchQueries: true
  });

  // Process conversations to apply local read status
  const processedConversations = React.useMemo(() => {
    if (!effectiveData?.getPersonalConversations) return [];

    return effectiveData.getPersonalConversations.map(conv => {
      // If this conversation was marked as read locally, override the server data
      if (readConversationIds.has(conv.id) && conv.lastMessages?.[0]) {
        return {
          ...conv,
          lastMessages: conv.lastMessages.map(msg => ({
            ...msg,
            read: true
          }))
        };
      }
      return conv;
    });
  }, [effectiveData?.getPersonalConversations, readConversationIds]);

  const handleConversationSelect = async (conversationId) => {
    setSelectedConversationId(conversationId);
    
    // Add to local read set immediately for persistent UI state
    setReadConversationIds(prev => new Set([...prev, conversationId]));
    
    // Call the GraphQL mutation to mark messages as read in the database
    // The mutation has refetchQueries configured to automatically refresh the conversation list
    try {
      const result = await markMessagesAsRead({
        variables: { conversationId }
      });
      
    } catch (e) {
      console.error('❌ Could not mark messages as read:', e);
      // Don't remove from local read set - keep optimistic UI
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleConversationUpdate = (updatedConversation) => {
    const currentUserIsParticipant1 = isSameUser(updatedConversation?.participant1?.id, currentUser?.id);
    const isBlockedByMe = currentUserIsParticipant1 
      ? updatedConversation?.blockedByParticipant1 
      : updatedConversation?.blockedByParticipant2;
    const isArchivedByMe = currentUserIsParticipant1
      ? updatedConversation?.archivedByParticipant1
      : updatedConversation?.archivedByParticipant2;
    
    if ((isBlockedByMe || isArchivedByMe) && String(selectedConversationId) === String(updatedConversation?.id)) {
      const inboxConversations = (effectiveData?.getPersonalConversations || []).filter(conv => {
        if (String(conv.id) === String(updatedConversation?.id)) return false;
        const isP1 = String(conv.participant1?.id) === String(currentUser?.id);
        const blocked = isP1 ? (conv.blockedByParticipant1 || conv.blockedByParticipant2) : (conv.blockedByParticipant2 || conv.blockedByParticipant1);
        const archived = isP1 ? conv.archivedByParticipant1 : conv.archivedByParticipant2;
        return !blocked && !archived;
      });

      if (inboxConversations.length > 0) {
        const sorted = [...inboxConversations].sort((a, b) => {
          const aTime = new Date(a.lastMessages?.[0]?.createdAt || '1970-01-01');
          const bTime = new Date(b.lastMessages?.[0]?.createdAt || '1970-01-01');
          return bTime - aTime;
        });
        setSelectedConversationId(sorted[0].id);
      } else {
        setSelectedConversationId(null);
      }

      refetch();
    }
  };

  // Function to get room_id from URL query parameters
  const getRoomIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('conversation_id') || urlParams.get('room_id');
  };

  // Function to find and auto-select a specific conversation by ID
  const autoSelectConversationById = (conversations, conversationId) => {
    if (!conversations || conversations.length === 0) return false;
    
    // Find conversation with matching ID
    const targetConversation = conversations.find(conv => 
      conv.id === conversationId || conv.id === parseInt(conversationId, 10) || conv.id.toString() === conversationId
    );
    
    if (targetConversation) {
      setSelectedConversationId(targetConversation.id);
      setHasAutoSelected(true);
      return true;
    } else {
      return false;
    }
  };

  // Function to find and auto-select the most recent conversation
  const autoSelectMostRecentConversation = (conversations) => {
    if (!conversations || conversations.length === 0 || hasAutoSelected) return;
    
    // Find conversation with the most recent message
    const conversationsWithMessages = conversations.filter(conv => 
      conv.lastMessages && conv.lastMessages.length > 0
    );
    
    if (conversationsWithMessages.length === 0) {
      // If no conversations have messages, select the first one
      setSelectedConversationId(conversations[0].id);
      setHasAutoSelected(true);
      return;
    }
    
    // Sort by last message timestamp and select the most recent
    const mostRecent = conversationsWithMessages.sort((a, b) => {
      const timeA = new Date(a.lastMessages[0].createdAt);
      const timeB = new Date(b.lastMessages[0].createdAt);
      return timeB - timeA; // Most recent first
    })[0];
    setSelectedConversationId(mostRecent.id);
    setHasAutoSelected(true);
  };

  // Use useEffect to handle auto-selection when conversations load
  useEffect(() => {
    // Don't reset if we have an initialConversationId
    if (!initialConversationId) {
      setHasAutoSelected(false);
    }
  }, [initialConversationId]);

  // Auto-select conversation when data loads
  // Priority: 1. initialConversationId prop, 2. conversation_id/room_id from URL, 3. Most recent
  useEffect(() => {
    if (effectiveData?.getPersonalConversations && !hasAutoSelected) {
      // Priority 1: Use initialConversationId if provided
      if (initialConversationId) {
        const foundConversation = autoSelectConversationById(effectiveData.getPersonalConversations, initialConversationId);
        if (!foundConversation) {
          // If initial conversation not found, fall back to most recent
          autoSelectMostRecentConversation(effectiveData.getPersonalConversations);
        }
      } else {
        // Priority 2: Check URL for conversation_id or room_id
        const conversationId = getRoomIdFromUrl();
        
        if (conversationId) {
          // Try to select conversation by ID from URL
          const foundConversation = autoSelectConversationById(effectiveData.getPersonalConversations, conversationId);
          
          // If URL conversation not found, fall back to most recent
          if (!foundConversation) {
            autoSelectMostRecentConversation(effectiveData.getPersonalConversations);
          }
        } else {
          // Priority 3: No specific conversation specified, select most recent
          autoSelectMostRecentConversation(effectiveData.getPersonalConversations);
        }
      }
    }
  }, [effectiveData, hasAutoSelected, initialConversationId]);

  return (
    <div className="h-full flex flex-col bg-[var(--succession-bg-primary)] overflow-hidden">
      {/* Page Header - Demo style */}
      <div className="flex-shrink-0 px-8 py-6 border-b border-[var(--succession-border)] bg-[var(--succession-bg-primary)]">
        <h1 className="text-2xl font-bold text-[var(--succession-text-primary)] m-0">{intl.formatMessage({ id: 'succession.messaging.hub.title' })}</h1>
        <p className="text-sm text-[var(--succession-text-secondary)] mt-1 m-0">{intl.formatMessage({ id: 'succession.messaging.hub.subtitle' })}</p>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {(() => {
          if (loading) {
            return (
              <>
                <div className="w-96 border-r border-[var(--succession-border)] bg-[var(--succession-bg-primary)] animate-pulse" />
                <div className="flex-1 bg-[var(--succession-bg-primary)] animate-pulse" />
              </>
            );
          }

          if (error && !effectiveData?.getPersonalConversations) {
            return (
              <div className="flex-1 flex items-center justify-center bg-[var(--succession-bg-primary)]">
                <div className="text-center">
                  <p className="text-red-500 mb-4">{intl.formatMessage({ id: 'succession.messaging.hub.failed_to_load' })}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-[#4A90E2] text-white px-4 py-2 rounded-lg hover:bg-[#3A7BC8] transition-colors"
                  >
                    {intl.formatMessage({ id: 'succession.messaging.hub.try_again' })}
                  </button>
                </div>
              </div>
            );
          }

          return (
            <>
              {/* Conversations Sidebar - Clean demo style */}
              <div className="w-96 flex-shrink-0 border-r border-[var(--succession-border)] flex flex-col h-full bg-[var(--succession-bg-primary)] overflow-hidden">
                <ConversationPanel
                  currentUser={currentUser}
                  selectedConversationId={selectedConversationId}
                  onConversationSelect={handleConversationSelect}
                  searchQuery={searchQuery}
                  theme={successionTheme}
                  conversations={processedConversations}
                  loading={false}
                  error={null}
                  onRefresh={() => window.location.reload()}
                />
              </div>

              {/* Chat Area - Clean demo style */}
              <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden bg-[var(--succession-bg-primary)]">
                {selectedConversationId ? (
                  <ChatPanel
                    currentUser={currentUser}
                    conversationId={selectedConversationId}
                    theme={successionTheme}
                    onConversationUpdate={handleConversationUpdate}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-[var(--succession-bg-secondary)]">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-[var(--succession-bg-tertiary)] flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-[var(--succession-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <p className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.messaging.hub.select_conversation' })}</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

MessagingHub.propTypes = {
  currentUser: PropTypes.object,
  theme: PropTypes.oneOf(['light', 'dark']),
  initialConversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialHiringPostId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default MessagingHub; 